import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Cabecera del calendario de fecha de entrega: el mes al centro y las flechas
 * de navegación a los costados, sin el selector de año de Material.
 */
@Component({
  selector: 'app-delivery-date-calendar-header',
  template: `
    <div class="delivery-date-calendar-header">
      <button
        type="button"
        class="delivery-date-calendar-nav"
        aria-label="Mes anterior"
        [disabled]="!previousEnabled"
        (click)="previousMonth()"
      >
        <span class="material-symbols-rounded">chevron_left</span>
      </button>

      <span class="delivery-date-calendar-period">{{ periodLabel }}</span>

      <button
        type="button"
        class="delivery-date-calendar-nav"
        aria-label="Mes siguiente"
        [disabled]="!nextEnabled"
        (click)="nextMonth()"
      >
        <span class="material-symbols-rounded">chevron_right</span>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryDateCalendarHeaderComponent implements OnDestroy {
  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly calendar: MatCalendar<Date>,
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(MAT_DATE_FORMATS) private readonly dateFormats: MatDateFormats
  ) {
    this.calendar.stateChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get periodLabel(): string {
    return this.dateAdapter.format(
      this.calendar.activeDate,
      this.dateFormats.display.monthYearLabel
    );
  }

  get previousEnabled(): boolean {
    if (!this.calendar.minDate) return true;

    return !this.isSameMonth(this.calendar.activeDate, this.calendar.minDate);
  }

  get nextEnabled(): boolean {
    if (!this.calendar.maxDate) return true;

    return !this.isSameMonth(this.calendar.activeDate, this.calendar.maxDate);
  }

  previousMonth(): void {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(
      this.calendar.activeDate,
      -1
    );
  }

  nextMonth(): void {
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(
      this.calendar.activeDate,
      1
    );
  }

  private isSameMonth(first: Date, second: Date): boolean {
    return (
      this.dateAdapter.getYear(first) === this.dateAdapter.getYear(second) &&
      this.dateAdapter.getMonth(first) === this.dateAdapter.getMonth(second)
    );
  }
}
